import type { z } from 'zod'

import { formatFileSize } from '~/utils/formatFileSize'
import { PhoneNumber } from '~/utils/PhoneNumber'

type RawIssue = z.core.$ZodRawIssue

/** Structurally identical to composables/TranslateFn; any TranslateFn value satisfies it. */
type Translate = (key: string, params?: Record<string, string | number | boolean>) => string

const isBoundedByLength = (origin: string): boolean => origin === 'string' || origin === 'array'

const expectedFormatFor = (format: string, t: Translate): string => {
  if (format === 'email') return t('presentation.errors.expectedFormat.email')
  if (format === 'url') return t('presentation.errors.expectedFormat.url')
  return t('presentation.errors.expectedFormat.value')
}

const AFFIRMATIVE_TOKENS: ReadonlySet<unknown> = new Set(['yes', 'on', '1', 1, 'true', true])

/** A rule the client cannot name reads as not allowed, never as a generic invalid value. */
const unmappedRuleMessage = (t: Translate): string => t('presentation.errors.field.notAllowed')

/** A consent box left unchecked fails single affirmative literals; a multi-value mismatch is an enum. */
const literalMismatchMessage = (
  valueSets: readonly (readonly unknown[])[],
  t: Translate
): string =>
  valueSets.every(([only, ...rest]) => rest.length === 0 && AFFIRMATIVE_TOKENS.has(only))
    ? t('presentation.errors.field.mustAccept')
    : t('presentation.errors.field.invalidEnumValue', {
        allowed_values: valueSets.flat().map(String).join(', ')
      })

const unionMessage = (
  errors: readonly (readonly (RawIssue | z.core.$ZodIssue)[])[],
  t: Translate
): string => {
  const literals = errors.flatMap((branch) => {
    const [only] = branch
    return branch.length === 1 && only?.code === 'invalid_value' ? [only.values] : []
  })
  return literals.length === errors.length && literals.length > 0
    ? literalMismatchMessage(literals, t)
    : unmappedRuleMessage(t)
}

const customMessageFor = (params: unknown, t: Translate): string => {
  const phoneVerdict = PhoneNumber.ruleVerdict(params)
  if (phoneVerdict !== undefined) return t(PhoneNumber.messageKey(phoneVerdict))
  if (typeof params === 'object' && params !== null && 'file_rule' in params) {
    if (params.file_rule === 'mimes' && 'types' in params) {
      return t('presentation.errors.field.invalidFileType', {
        allowed_values: String(params.types)
      })
    }
    if (params.file_rule === 'max' && 'max' in params) {
      return t('presentation.errors.field.fileTooLarge', {
        max: formatFileSize(Number(params.max) * 1024)
      })
    }
  }
  return unmappedRuleMessage(t)
}

const invalidTypeMessage = (
  issue: { readonly expected: unknown; readonly input?: unknown },
  t: Translate
): string =>
  issue.input === undefined || issue.input === null
    ? t('presentation.errors.field.required')
    : t('presentation.errors.field.invalidType', { expected_type: String(issue.expected) })

const sizeBoundMessage = (
  issue: z.core.$ZodRawIssue<z.core.$ZodIssueTooSmall | z.core.$ZodIssueTooBig>,
  t: Translate
): string => {
  const isMinimumBound = issue.code === 'too_small'
  const bound = Number(isMinimumBound ? issue.minimum : issue.maximum)
  const isLengthBound = isBoundedByLength(issue.origin)
  if (!isMinimumBound) {
    return isLengthBound
      ? t('presentation.errors.field.tooLong', { max: bound })
      : t('presentation.errors.field.atMost', { max: bound })
  }
  if (!isLengthBound) return t('presentation.errors.field.atLeast', { min: bound })
  return bound === 1
    ? t('presentation.errors.field.required')
    : t('presentation.errors.field.tooShort', { min: bound })
}

const formatAndLiteralMessage = (issue: RawIssue, t: Translate): string => {
  switch (issue.code) {
    case 'invalid_format':
      return t('presentation.errors.field.invalidFormat', {
        expected_format: expectedFormatFor(issue.format, t)
      })
    case 'invalid_value':
      return literalMismatchMessage([issue.values], t)
    case 'invalid_union':
      return unionMessage(issue.errors, t)
    default:
      return unmappedRuleMessage(t)
  }
}

const boundMessage = (issue: RawIssue, t: Translate): string => {
  switch (issue.code) {
    case 'invalid_type':
      return invalidTypeMessage(issue, t)
    case 'too_small':
    case 'too_big':
      return sizeBoundMessage(issue, t)
    default:
      return formatAndLiteralMessage(issue, t)
  }
}

export const zodErrorMap = (t: Translate): ((issue: RawIssue) => string | undefined) => {
  return (issue) =>
    issue.code === 'custom' ? customMessageFor(issue.params, t) : boundMessage(issue, t)
}
